import type {
  ElementContent,
  Nodes as HastNodes,
  Root as HastRoot,
} from "hast";
import type { Root as MdastRoot } from "mdast";
import type { Handlers } from "mdast-util-to-hast";
import { defaultHandlers, toHast } from "mdast-util-to-hast";
import { withMdastOrigin } from "./provenance";

/**
 * Generic MDAST → HAST conversion for the v2 markdown pipeline (§6.1).
 *
 * Converts with `allowDangerousHtml: true` so embedded raw HTML (e.g. saved
 * `<mark>` highlights) survives as `raw` nodes for `rehypeRaw` downstream. The
 * `code`/`inlineCode` default handlers are wrapped to tag their output with the
 * transient provenance marker, so the term-highlight pass skips MDAST-originated
 * code while still highlighting raw-HTML `<code>` (§6.3). Feature-specific
 * handlers (e.g. the report citation `link` handler) are merged via `handlers`.
 */

/** Return type of a `mdast-util-to-hast` handler. */
type HandlerResult = ElementContent | Array<ElementContent> | undefined;

/** Tags an MDAST-originated element result with the transient provenance marker. */
export function markOrigin(result: HandlerResult): HandlerResult {
  if (result && !Array.isArray(result) && result.type === "element") {
    result.properties = withMdastOrigin(result.properties);
  }
  return result;
}

const provenanceHandlers: Partial<Handlers> = {
  code(state, node) {
    return markOrigin(defaultHandlers.code(state, node));
  },
  inlineCode(state, node) {
    return markOrigin(defaultHandlers.inlineCode(state, node));
  },
};

/**
 * Converts a validated MDAST root to a HAST root, merging the provenance
 * `code`/`inlineCode` handlers with any feature-provided handlers.
 */
export function mdastToHast(
  root: MdastRoot,
  handlers?: Partial<Handlers>
): HastRoot {
  const hast: HastNodes = toHast(root, {
    allowDangerousHtml: true,
    handlers: { ...provenanceHandlers, ...handlers },
  });
  // `toHast` on a `root` always yields a hast `root`; normalise defensively.
  return hast.type === "root" ? hast : { type: "root", children: [hast] };
}
